import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NotificationService } from '../../services/notification.service';
import { concat, EMPTY, Observable } from 'rxjs';
import { catchError, filter, last, map, switchMap, withLatestFrom } from 'rxjs/operators';

import {
  AddSignatureMatrixTemplate,
  AddUnsignedSignatureMatrices,
  CertificateActionTypes,
  CertificateDetailsPatch,
  CertificateSignatureMatrixSave,
  CheckSheetActionTypes,
  CloneSignatureMatrixTemplates,
  DeleteSignatureMatrix,
  DeleteSignatureMatrixTemplate,
  FetchDefaultSignatureMatrices,
  FetchDefaultSignatureMatricesSuccess,
  HandoverPatch,
  HandoverSignatureMatrixSave,
  InsertAndSignSignatureMatrix,
  RefreshCurrentCsaSignatureMatrix,
  SaveSignatureMatrix,
  SignatureMatrixActionTypes,
  SignatureSetupCopy,
  SignatureSetupCopySourcesLoad,
  SignatureSetupCopySourcesLoadSuccess,
  SignatureSetupCopySuccess,
  SigntureMatrixTemplateLoadSuccess,
  ToggleSignSignatureMatrix,
  UpdateSignatureMatrixSuccess,
  UpdateSignatureMatrixTemplate,
} from '@completion/actions';
import { State } from '@completion/reducers';
import { getCurrentCpId, getCurrentCsaId, getCurrentMcpId, getCurrentProject } from '@completion/selectors';
import { CpService, McpService, SignatureMatrixService } from '@completion/services';
import { Project } from '@completion/models';
import { SignatureType } from '@completion/enums';
import { trackResource } from './resource-fetch.util';

@Injectable()
export class SignatureMatrixEffect {
  constructor(
    private readonly actions$: Actions,
    private readonly signatureMatrixService: SignatureMatrixService,
    private readonly cpService: CpService,
    private readonly mcpService: McpService,
    private readonly store: Store<State>,
    private readonly toastr: NotificationService,
  ) {}

  certificateSaveSignatureMatrix$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CertificateSignatureMatrixSave>(SignatureMatrixActionTypes.CertificateSignatureMatrixSave),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      filter(([_, currentProject, cpId]) => !!(currentProject && cpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'certificateSaveSignatureMatrix',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.certType, action.certId, action.matrix],
        onSuccess: (response) => new CertificateDetailsPatch(response as any),
      }),
    ),
  );

  handoverSaveSignatureMatrix$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverSignatureMatrixSave>(SignatureMatrixActionTypes.HandoverSignatureMatrixSave),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      filter(([_, currentProject, cpId]) => !!(currentProject && cpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'handoverSaveSignatureMatrix',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.handoverId, action.matrix],
        onSuccess: (response) => new HandoverPatch(response as any),
      }),
    ),
  );

  fetchDefaultSignatureMatrices$ = createEffect(() =>
    this.actions$.pipe(
      ofType<FetchDefaultSignatureMatrices>(SignatureMatrixActionTypes.FetchDefaultSignatureMatrices),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'fetchDefaultSignatureMatrices',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new FetchDefaultSignatureMatricesSuccess(response as any),
      }),
    ),
  );

  deleteSignatureMatrixTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteSignatureMatrixTemplate>(SignatureMatrixActionTypes.DeleteSignatureMatrixTemplate),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'deleteSignatureMatrixTemplate',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.matrix.id],
        onSuccess: (response) => new SigntureMatrixTemplateLoadSuccess(response as any),
      }),
    ),
  );

  updateSignatureMatrixTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateSignatureMatrixTemplate>(SignatureMatrixActionTypes.UpdateSignatureMatrixTemplate),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'updateSignatureMatrixTemplate',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.matrix],
        onSuccess: (response) => new SigntureMatrixTemplateLoadSuccess(response as any),
      }),
    ),
  );

  addSignatureMatrixTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AddSignatureMatrixTemplate>(SignatureMatrixActionTypes.AddSignatureMatrixTemplate),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'addSignatureMatrixTemplate',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.matrix],
        onSuccess: (response) => new SigntureMatrixTemplateLoadSuccess(response as any),
      }),
    ),
  );

  addUnsignedSignatureMatrices$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AddUnsignedSignatureMatrices>(SignatureMatrixActionTypes.AddUnsignedSignatureMatrices),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'addUnsignedSignatureMatrices',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.matrix],
        onSuccess: () => null,
      }),
    ),
  );

  saveSignatureMatrix$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SaveSignatureMatrix>(SignatureMatrixActionTypes.SaveSignatureMatrix),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'saveSignatureMatrix',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.matrix],
        // The save endpoint already returns the persisted, up-to-date signature matrix - use it
        // directly instead of triggering a separate full-CSA refetch (RefreshCurrentCsaSignatureMatrix),
        // which was slower and raced with the dialog closing, so the UI didn't reliably reflect
        // the change until some later, unrelated re-render.
        // This action/effect is shared by the "Manage Signatures" dialog for BOTH CSA and
        // Certificate (MCC/RFCC/RFOC) signature types, but `UpdateSignatureMatrixSuccess` is only
        // handled by the check-sheet reducer. Without branching here, saving a certificate's
        // signature matrix via that dialog silently did nothing to the store (certificate reducer
        // has no case for it) until a full page reload. Route certificate types through the same
        // CertificateDetailsPatch helper used by the toggle/insert-and-sign effects below.
        onSuccess: (response, [source]) => {
          const signatureType: SignatureType = source?.[0]?.signatureType;
          if (signatureType === SignatureType.MCC || signatureType === SignatureType.RFCC || signatureType === SignatureType.RFOC) {
            return this.certificateSignatureMatrixPatch(signatureType, response as any);
          }
          return new UpdateSignatureMatrixSuccess(response as any);
        },
      }),
    ),
  );

  deleteSignatureMatrix$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteSignatureMatrix>(SignatureMatrixActionTypes.DeleteSignatureMatrix),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'deleteSignatureMatrix',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.matrixId],
        // Same as above - the delete endpoint already returns the resulting signature matrix, and
        // certificate types need to be routed to CertificateDetailsPatch instead of
        // UpdateSignatureMatrixSuccess (which only the check-sheet reducer handles).
        onSuccess: (response, [source]) => {
          const signatureType: SignatureType = source?.[0]?.signatureType;
          if (signatureType === SignatureType.MCC || signatureType === SignatureType.RFCC || signatureType === SignatureType.RFOC) {
            return this.certificateSignatureMatrixPatch(signatureType, response as any);
          }
          return new UpdateSignatureMatrixSuccess(response as any);
        },
      }),
    ),
  );

  insertAndSignCertificateSignatureMatrix$ = createEffect(() =>
    this.actions$.pipe(
      ofType<InsertAndSignSignatureMatrix>(CertificateActionTypes.InsertAndSignSignatureMatrix),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'insertAndSignSignatureMatrix',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.parentId, [action.entry], action.unsignedEntries],
        onSuccess: (response, [source]) => this.certificateSignatureMatrixPatch(source[0].entry.signatureType, response as any),
      }),
    ),
  );

  toggleSignSignatureMatrix$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ToggleSignSignatureMatrix>(SignatureMatrixActionTypes.ToggleSignSignatureMatrix),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'toggleSignSignatureMatrix',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.matrixId],
        onSuccess: (response, [source]) => this.certificateSignatureMatrixPatch(source[0].signatureType, response as any),
      }),
    ),
  );

  private certificateSignatureMatrixPatch(signatureType: SignatureType, signatureMatrix: any) {
    switch (signatureType) {
      case SignatureType.MCC:
        return new CertificateDetailsPatch({ signatureMatrix });
      case SignatureType.RFCC:
        return new CertificateDetailsPatch({ signatureMatrix });
      case SignatureType.RFOC:
        return new CertificateDetailsPatch({ signatureMatrix });
      default:
        return null;
    }
  }

  cloneSignatureMatrixTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CloneSignatureMatrixTemplates>(SignatureMatrixActionTypes.CloneSignatureMatrixTemplates),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      switchMap(([action, currentProject]) => {
        const requests = action.templates.map((template) =>
          this.signatureMatrixService.addSignatureMatrixTemplate(currentProject.id, template),
        );

        return concat(...requests).pipe(
          last(),
          switchMap((finalResult) => [new SigntureMatrixTemplateLoadSuccess(finalResult as any)]),
          catchError((err) => {
            this.toastr.error(err?.error?.message || 'Failed to clone templates');
            return EMPTY;
          }),
        );
      }),
    ),
  );

  loadSignatureSetupCopySources$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SignatureSetupCopySourcesLoad>(SignatureMatrixActionTypes.SignatureSetupCopySourcesLoad),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        service: this.signatureMatrixService,
        method: 'getSignatureSetupCopySources',
        serviceArgs: (action) => [action.targetProjectId],
        onSuccess: (sources) => new SignatureSetupCopySourcesLoadSuccess(sources as any),
      }),
    ),
  );

  copySignatureSetup$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SignatureSetupCopy>(SignatureMatrixActionTypes.SignatureSetupCopy),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        service: this.signatureMatrixService,
        method: 'copySignatureSetup',
        serviceArgs: (action) => [action.targetProjectId, { sourceProjectId: action.sourceProjectId }],
        onSuccess: (result) => new SignatureSetupCopySuccess(result as any),
      }),
    ),
  );

  refreshCurrentCsaSignatureMatrix$ = createEffect(() =>
    this.actions$.pipe(
      ofType<RefreshCurrentCsaSignatureMatrix>(CheckSheetActionTypes.RefreshCurrentCsaSignatureMatrix),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number>,
        this.store.select(getCurrentMcpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_, project, cpId, mcpId, csaId]) => !!(project && csaId && (cpId || mcpId))),
      switchMap(([_, project, cpId, mcpId, csaId]) => {
        const csaRequest$ = mcpId
          ? this.mcpService.getCheckSheetAssignment(project.id, mcpId, csaId)
          : this.cpService.getCheckSheetAssignment(project.id, cpId, csaId);
        return csaRequest$.pipe(map((csa) => new UpdateSignatureMatrixSuccess(csa.signatureMatrix || [])));
      }),
    ),
  );
}
